import type {ReactElement} from 'react'
import {useState} from 'react'
import {exhaustive} from '@freckle/exhaustive'
import {maybe} from '@freckle/maybe'
import {Link} from '@freckle/educator-materials/ts/components/link'
import {Modal, Header, Body} from '@freckle/educator-materials/ts/components/modal'
import {Text} from '@freckle/educator-materials/ts/components/typography'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import type {TeacherAttrs} from '@freckle/classroom/ts/users/models/teacher'
import {MarketoForm} from '../marketo-form'

type StepT = {tag: 'quote-form'} | {tag: 'quote-success'; followUpUrl: string}

export type Props = {
  show: boolean
  onHide: () => void
  teacher: TeacherAttrs
}

export function QuoteRequestModal({show, teacher, onHide}: Props) {
  const {t} = useSafeTranslation()

  const [step, setStep] = useState<StepT>({tag: 'quote-form'})

  function onSuccess(a: {mktoFormId: string; followUpUrl: string}): void {
    const {followUpUrl} = a
    setStep({tag: 'quote-success', followUpUrl})
  }

  function MarketoFormWrapper() {
    const countryCode = maybe(
      () => null,
      teacher => teacher.countryCode,
      teacher,
    )
    return <MarketoForm onSuccess={onSuccess} countryCode={countryCode} />
  }

  let modalAttrs: {
    dataTest: string
    header: string
    body: ReactElement
    ctaButtons: ReactElement[]
  }
  switch (step.tag) {
    case 'quote-form':
      modalAttrs = {
        dataTest: 'quote-request-modal',
        header: t('QUOTE_REQUEST_MODAL_QUOTE_FORM_HEADER'),
        body: <MarketoFormWrapper />,
        ctaButtons: [],
      }
      break
    case 'quote-success':
      modalAttrs = {
        dataTest: 'quote-request-modal-success',
        header: t('QUOTE_REQUEST_MODAL_QUOTE_SUCCESS_HEADER'),
        body: (
          <>
            <Text>{t('QUOTE_REQUEST_MODAL_QUOTE_SUCCESS_THANKS_COPY')}</Text>
          </>
        ),
        ctaButtons: [
          <Link
            key="quote-request-modal-success-calendar-link"
            style="btn-primary"
            href={step.followUpUrl}
            target="_blank"
            rel="noopener"
            dataTest="quote-request-modal-success-calendar-link"
            reactRouter={false}
          >
            {t('QUOTE_REQUEST_MODAL_QUOTE_SUCCESS_SCHEDULE_BUTTON')}
          </Link>,
        ],
      }
      break
    default:
      modalAttrs = exhaustive(step, 'render')
  }

  return (
    <Modal show={show} onHide={onHide} width="medium-small" customStyle={{height: '55vh'}}>
      <Header>{modalAttrs.header}</Header>
      <Body>{modalAttrs.body}</Body>
    </Modal>
  )
}
